import React, { useState } from "react";
import { Box, Button, Typography, styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import WarningIcon from "@icons/myicons/warning-circle-filled.svg?react";
import { currentReturnUrl, useRoutes } from "@hooks";
import { useSmartbrokerPlusExpired } from "@hooks/useSmartbrokerPlusExpired";
import { accountRowId } from "@components/Accounts";
import { CloseButton } from "@components/Modal";

const DISMISSED_KEY = "smartbrokerPlusExpiredNoticeDismissed";

/** The Smartbroker+ entry of the bank whitelist (`BDWBDEMM003`). */
const SMARTBROKER_PLUS_BANK_SLUG = "SmartbrokerPlus";

/**
 * Floating notice, bottom right, above the app. Mirrors the bank import status
 * sticky so both float at the same spot with the same elevation.
 */
const Wrapper = styled(Box)(({ theme }) => ({
  zIndex: 10000,
  position: "fixed",
  bottom: theme.spacing(6),
  right: theme.spacing(6),
  display: "flex",
  alignItems: "center",
  width: "580px",
  maxWidth: `calc(100vw - ${theme.spacing(8)})`,
  gap: theme.spacing(2.5),
  padding: theme.spacing(3, 5, 3, 4),
  backgroundColor: theme.palette.background.warning,
  boxShadow: theme.shadows[2],
  borderRadius: `${theme.shape.borderRadius}px`,
  boxSizing: "border-box",

  [theme.breakpoints.down("sm")]: {
    bottom: theme.spacing(4),
    right: theme.spacing(4),
    left: theme.spacing(4),
    width: "auto",
  },
}));

export const SmartbrokerPlusExpiredSticky = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isExpired, variant, accountId } = useSmartbrokerPlusExpired();
  const { pushImportConnection } = useRoutes();
  const [isDismissed, setIsDismissed] = useState(
    () => sessionStorage.getItem(DISMISSED_KEY) === "true"
  );

  const dismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, "true");
    setIsDismissed(true);
  };

  const handleCtaClick = () => {
    if (variant === "outdated") {
      // The depot is still connected, so send the user to its row in the list,
      // where the update action for its bank connection sits. Dismissing on the
      // way keeps the notice from floating over the very row it points at —
      // and the user has acted on it, so repeating it adds nothing.
      dismiss();
      navigate(
        accountId
          ? `/depots-accounts#${accountRowId(accountId)}`
          : "/depots-accounts"
      );
      return;
    }

    // The depot is gone: there is nothing to update, so start a fresh import.
    // For WEALTH_API this leaves the app for the Smartbroker+ web form login.
    // The return url is handed over directly rather than through session
    // storage, which the redirect reads before this tick's write reaches it.
    void pushImportConnection(
      SMARTBROKER_PLUS_BANK_SLUG,
      "WEALTH_API",
      currentReturnUrl()
    );
  };

  if (!isExpired || isDismissed) return null;

  return (
    <Wrapper data-testid="smartbroker-plus-expired-sticky">
      <Box
        display="flex"
        alignItems="center"
        alignSelf="flex-start"
        color="icon.warning"
        mt={1}
        sx={{ "& svg": { flexShrink: 0 } }}
      >
        <WarningIcon width={24} height={24} />
      </Box>
      <Box display="flex" flexDirection="column" flex={1} gap={3} minWidth={0}>
        <Box display="flex" alignItems="flex-start" gap={2.5}>
          <Box flex={1} minWidth={0}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {t("premium.smartbrokerPlusExpired.title")}
            </Typography>
            <Typography
              variant="body3"
              color="textSecondary"
              whiteSpace="pre-line"
            >
              {t(`premium.smartbrokerPlusExpired.${variant}.text`)}
            </Typography>
          </Box>
          <CloseButton
            onClose={dismiss}
            aria-label={t("premium.smartbrokerPlusExpired.close")}
            data-testid="smartbroker-plus-expired-sticky-close"
            sx={{ p: 0, flexShrink: 0, color: "icon.secondary" }}
          />
        </Box>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleCtaClick}
          data-testid="smartbroker-plus-expired-sticky-cta"
        >
          {t(`premium.smartbrokerPlusExpired.${variant}.cta`)}
        </Button>
      </Box>
    </Wrapper>
  );
};
